import React, { useEffect, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { RouteComponentProps, withRouter } from "react-router";
import moment from "moment";
import { KeyboardArrowDown } from "@mui/icons-material";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";
import { push } from "react-router-redux";

import EditorWrapperLayout from "modules/common/components/authoring/editorLayouts/editorWrapperLayout";
import MainEditorLayout from "modules/common/components/authoring/editorLayouts/mainEditorLayout";
import LoadingOverlay from "modules/common/components/loadingOverlay";
import BasePage from "pages/common/basePage";
import EmailEditor from "./emailEditor";
import EmailSettingsPanel from "./emailSettingsPanel";
import AuthoringAppBar from "modules/common/components/authoring/authoringAppBar";
import { BackItem } from "pages/common/breadcrumb";
import EmailAuthoringActions from "./emailAuthoringEditorActions";
import ContentGuidelinePreview from "modules/settings/components/content/guidelines/contentGuidelinePreview";
import { HintType } from "modules/common/components/authoring/dialogs/guidelineHint";
import { createNewEmail, DEFAULT_EMAIL, emailsSlice, getEmailToEdit, saveExistingEmail } from "modules/emails/reducer";
import { DEFAULT_EMAIL_TRANSLATABLE_CONTENT, IEmail, IEmailTemplate, IEmailTranslatableContent } from "modules/emails/models";
import { actions as NewsletterActions, Address } from "modules/newsletters";
import { actions as AudienceActions } from "modules/audiences";
import ConfirmDialog from "modules/common/components/dialogs/confirmDialog";
import EmailOneLastCheck from "../emailOneLastCheck";
import { adminFilesApi, cognitiveServicesApi, emailsApi } from "api/instances";
import { PickerLocalization } from "modules/common/components/pickerLocalization";
import { useAudienceCount } from "modules/common/hooks/useAudienceCount";
import Loading from "modules/common/components/loading";
import { useSetSnackbarMessages } from "modules/common/hooks/useSetSnackbarMessages";
import { useDisplayNav } from "modules/common/hooks/useDisplayNav";
import EmailPreview from "../emailPreview";
import { useOpenContentGuidelines } from "modules/common/hooks/useOpenContentGuidelines";
import { htmlIsEqual } from "utils/htmlIsEqual";
import ShallowEquals from "utils/shallowEquals";
import { IdNamePair, ITranslatedText, TranslationContentType, TranslationTextFormat } from "modules/common/models";
import { AttachedFile } from "modules/gallery";
import useLanguages from "modules/common/hooks/data/useLanguages";
import LanguageBarWithTranslateDialog from "modules/common/components/languageBarWithTranslateDialog";
import { ILanguage, ILcidTranslateDetails } from "modules/localization/models";
import TranslateAllIssueDialog from "modules/settings/components/translateAllIssueDialog";
import useTranslateAll from "modules/common/hooks/data/useTranslateAll";

import "../../styles/emailEditorPage.sass";
import "../../../common/components/authoring/styles/authoringLayout.sass";

interface IEmailEditorPageProps {}

const EmailEditorPage: React.FC<PropsWithRedux> = ({
    redirectTo,
    match,
    changedSinceSaved,
    isFetching,
    getEmailSenderConfig,
    email,
    getAudiences,
    isSaving,
    changedSinceLastSaved,
    shouldOpenOneLastCheck,
    tenantName
}) => {
    const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(true);
    const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [fromEmailAddresses, setFromEmailAddresses] = useState<Address[]>([]);
    const [configFetched, setConfigFetched] = useState(false);
    const [showDiscardDialog, setShowDiscardDialog] = useState(false);
    const [showOneLastCheck, setShowOneLastCheck] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [isFetchingTemplateHTMLData, setIsFetchingTemplateHTMLData] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const theme = useTheme();
    const isSmallAndSmaller = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
    const dispatch = useDispatch();

    const { audienceCount, isFetchingAudienceCount } = useAudienceCount({ audiences: email.audiences, sendToAllUsers: email.sendToAllUsers });
    const { openContentGuidelines, setOpenContentGuidelines } = useOpenContentGuidelines();

    const { redirectUrl, editor, emailTemplates } = useSelector((state: GlobalApplicationState) => state.emails);
    const { setSuccessMessage, setErrorMessage } = useSetSnackbarMessages();
    useDisplayNav(false, { backgroundColor: "#FFFFFF" });
    
    const memoizedEmailTranslations = React.useMemo(() => {
        let allContentTranslations: Set<string> = new Set();
        email.subject.forEach((subject: ITranslatedText) => allContentTranslations.add(subject.lcid));
        email.body.forEach((body: ITranslatedText) => allContentTranslations.add(body.lcid));

        return Array.from(allContentTranslations).reduce((map: Record<string, IEmailTranslatableContent>, lcid: string) => {
            const subject = email.subject.find(t => t.lcid === lcid)?.text || "";
            const body = email.body.find(t => t.lcid === lcid)?.text || "";
            return { ...map, [lcid]: ({ subject, body }) } as Record<string, IEmailTranslatableContent>;
        }, {});
    }, [email.subject, email.body]);

    const useLanguagesData = useLanguages(memoizedEmailTranslations);
    const { activeLcid, lcidMappings, defaultLcid, availableLanguages } = useLanguagesData;

    //Used to get around TenantRequest not being available in hooks
    const translateAllApiWrapper = async (inputTranslation: IEmailTranslatableContent, outputLanguages: ILcidTranslateDetails[]) => {
        return await cognitiveServicesApi.translateAllEmailLanguages(inputTranslation, outputLanguages);
    }

    const { isFetchingTranslations: isFetchingTranslation, errorModalStatus, translateAll } = useTranslateAll ({
        translatedContent: memoizedEmailTranslations[defaultLcid],
        defaultLcid,
        availableLanguages,
        getAllTranslations: translateAllApiWrapper
    });

    const setShouldOpenOneLastCheck = (shouldOpen: boolean) => 
        dispatch({ type: emailsSlice.actions.SET_SHOULD_OPEN_ONE_LAST_CHECK, payload: { shouldOpen }});
    const showEmailSentConfirmation = (sentTime: string, emailId: string) => dispatch({ 
        type: emailsSlice.actions.SET_SHOW_SENT_CONFIRMATION, 
        payload: { 
            sentConfirmation: {
                shouldShowDialog: true,
                expectedSentTime: sentTime,
                emailId
            }
        }
    });
    
    const tenantId = match.params.tenant;
    const emailId = match.params.emailId;
    const isEmailIdPresent = React.useMemo(() => !!emailId && emailId !== "new", [emailId]);
    const isLoading = isFetching || !configFetched || !email.fromSender.email || !email.fromSender.name || isFetchingTemplateHTMLData;
    const isEmailValid = email.subject.length > 0 && 
        email.subject.every(s => !!s.text) && 
        (email.sendToAllUsers || email.audiences.length > 0) && 
        !!email.fromSender.email && 
        !!email.fromSender.name;

    const emailRequiresValidFromAddress = fromEmailAddresses.length > 0 && !email.fromSender.email && !email.fromSender.name;

    useEffect(() => {
        const getHtmlTemplateData = async (template: IEmailTemplate) => {
            try {
                setIsFetchingTemplateHTMLData(true);
                const templateData = await adminFilesApi.GetEmailTemplateFile(template.ospreyFileName);
                const reader = new FileReader();

                reader.onload = () => {
                    updateEmail({ body: [{ lcid: activeLcid, text: (reader.result as string) ?? "" }] });
                };
                
                reader.readAsText(templateData);
            }
            catch { }
            finally {
                setIsFetchingTemplateHTMLData(false);
            }
        }

        if (emailTemplates.selectedTemplateFileName && !isEmailIdPresent) {
            const emailTemplate = emailTemplates.templates.find(t => t.ospreyFileName === emailTemplates.selectedTemplateFileName);
            if (emailTemplate) {
                getHtmlTemplateData(emailTemplate);
            }
            dispatch({ type: emailsSlice.actions.SET_SELECTED_EMAIL_TEMPLATE_FILE_NAME, payload: { selectedTemplateFileName: undefined } });
        }
    }, [emailTemplates.selectedTemplateFileName, emailTemplates.templates, isEmailIdPresent]);

    useEffect(() => {
        const getAndSetInitialConfig = async () => {
            const config = await getEmailSenderConfig();
            setFromEmailAddresses(config.addresses.sort((a, b) => a.fromEmailName.localeCompare(b.fromEmailName)));
            setConfigFetched(true);
        }

        if (!configFetched)
            getAndSetInitialConfig();
        
        getAudiences();
    }, []);

    useEffect(() => {
        if (isEmailIdPresent) {
            dispatch(getEmailToEdit({ emailId }));
        }
    }, [isEmailIdPresent]);

    useEffect(() => {
        if (email.id && !changedSinceLastSaved) {
            if (shouldOpenOneLastCheck) {
                setShouldOpenOneLastCheck(false);
                setShowOneLastCheck(true);
            }
        }
    }, [email.id, changedSinceLastSaved, shouldOpenOneLastCheck]);

    const hasEmailUpdated = (updatedEmailFields: Partial<IEmail>): boolean => {
        const updatedEmail = { ...editor.email, ...updatedEmailFields };
        const originalEmail = editor.originalEmail;

        const audienceIsEqual = (newAudience: IdNamePair<string, string>[]): boolean => {
            const newAudienceIds = newAudience.map(a => a.id);
            const originalAudienceIds = originalEmail.audiences.map(a => a.id);
    
            if (newAudienceIds.length !== originalAudienceIds.length)
                return false;
    
            const originalAudIdSet = new Set(originalAudienceIds.sort());
    
            return newAudienceIds.every(id => originalAudIdSet.has(id));
        }

        const sentTimeIsEqual = (newSentTimeString?: string): boolean => {
            return newSentTimeString === originalEmail.sentTime || 
                moment(newSentTimeString).isSame(moment(originalEmail.sentTime), "minutes");
        }

        const fileAttachmentsIsEqual = (newFileAttachments: AttachedFile[]): boolean => {
            const newFileAttachmentIds = newFileAttachments.map(a => a.ospreyId);
            const originalFileAttachmentIds = originalEmail.fileAttachments.map(a => a.ospreyId);
    
            return ShallowEquals(newFileAttachmentIds.sort(), originalFileAttachmentIds.sort());
        }

        const emailBodiesIsEqual = (newBody: ITranslatedText[]): boolean => {
            const originalBody = originalEmail.body;

            if (newBody.length !== originalBody.length) 
                return false;

            for (const body of newBody) {
                const originalBodyText = originalBody.find(t => t.lcid === body.lcid)?.text;

                if (!htmlIsEqual(body.text, originalBodyText))
                    return false;
            }

            return true;
        }

        const emailSubjectsIsEqual = (newSubject: ITranslatedText[]): boolean => {
            const originalSubject = originalEmail.subject;

            if (newSubject.length !== originalSubject.length) 
                return false;
            
            for (const subject of newSubject) {
                const originalSubjectText = originalSubject.find(t => t.lcid === subject.lcid)?.text;
                
                if (!ShallowEquals(subject.text, originalSubjectText))
                    return false;
            }

            return true;
        }

        return !emailBodiesIsEqual(updatedEmail.body) ||
            !emailSubjectsIsEqual(updatedEmail.subject) ||
            !audienceIsEqual(updatedEmail.audiences) || 
            !ShallowEquals(updatedEmail.sendToAllUsers, originalEmail.sendToAllUsers) ||
            !ShallowEquals(updatedEmail.fromSender, originalEmail.fromSender) ||
            !ShallowEquals(updatedEmail.replyAddress, originalEmail.replyAddress) ||
            !sentTimeIsEqual(updatedEmail.sentTime) ||
            !fileAttachmentsIsEqual(updatedEmail.fileAttachments);
    }

    const updateEmail = (updatedEmailFields: Partial<IEmail>, checkIfChanged: boolean = true) => {
        dispatch({ 
            type: emailsSlice.actions.UPDATE_EMAIL_EDITOR, 
            payload: { 
                email: {
                    ...updatedEmailFields
                } as Partial<IEmail>,
                changedSinceLastSaved: checkIfChanged ? hasEmailUpdated(updatedEmailFields) : undefined
            }
        });
    }

    const updateOriginalEmail = (updatedEmailFields: Partial<IEmail>) => {      
        dispatch({ 
            type: emailsSlice.actions.UPDATE_ORIGINAL_EMAIL_EDITOR, 
            payload: { 
                originalEmail: {
                    ...updatedEmailFields
                } as Partial<IEmail>
            }
        });

        updateEmail(updatedEmailFields, false);
    }

    useEffect(() => {
        // set the fromSender to the first one in the 'from email' address list
        if (emailRequiresValidFromAddress) {
            const fromSender = { 
                email: fromEmailAddresses[0].fromEmailAddress, 
                name: fromEmailAddresses[0].fromEmailName 
            };
            
            updateOriginalEmail({ fromSender });
        }
    }, [fromEmailAddresses, emailRequiresValidFromAddress]);

    const manageEmailsUrl = `/${tenantId}/admin/emails`;

    useEffect(() => {   
        if (redirectUrl) {
            if (redirectUrl === manageEmailsUrl)
                redirectToManageEmails();
            else 
                redirectTo(redirectUrl);

            dispatch({ type: emailsSlice.actions.SET_REDIRECT_URL, payload: { redirectUrl: undefined }});
        }
    }, [redirectUrl, manageEmailsUrl]);

    const onToggleDrawer = () => setSettingsDrawerOpen(prev => !prev);

    const redirectToManageEmails = () => {
        dispatch({
            type: emailsSlice.actions.SET_EMAIL_EDITOR, 
            payload: { 
                email: DEFAULT_EMAIL
            }
        });
        dispatch({type: emailsSlice.actions.SET_TEST_EMAILS, payload: { testEmails: [] }});
        redirectTo(manageEmailsUrl);
    }

    const backToManagePosts = React.useCallback(() => {
        changedSinceSaved
            ? setShowUnsavedWarning(true)
            : redirectToManageEmails();
    }, [changedSinceSaved, redirectToManageEmails]);

    const onSendEmail = async () => {
        setIsSendingEmail(true);

        try {
            await emailsApi.sendEmail(email.id);
            showEmailSentConfirmation(
                email.sentTime && new Date(email.sentTime) >= new Date() 
                    ? email.sentTime
                    : new Date().toISOString(),
                email.id
            );
            redirectToManageEmails();
        }
        catch {
            setErrorMessage("There was an error. Please try again.");
            setIsSendingEmail(false);
            setShowOneLastCheck(false);
        }
    }

    const onClickSend = async () => {
        if (!email.id || changedSinceLastSaved) {
            saveEmail();
            setShouldOpenOneLastCheck(true);
        }
        else {
            setShowOneLastCheck(true);
        }
    }

    const saveEmail = (redirectUrl?: string) => {
        if (isLoading) return;

        isEmailIdPresent 
            ? dispatch(saveExistingEmail({ redirectUrl }))
            : dispatch(createNewEmail({ redirectUrl }));
    };

    const getActions = React.useMemo((): JSX.Element =>
        <EmailAuthoringActions
            onSend={onClickSend}
            isSendDisabled={!isEmailValid}
            onDiscard={() => changedSinceSaved && setShowDiscardDialog(true)}
            isDiscardDisabled={!changedSinceSaved}
            onPreview={() => setShowPreview(true)}
            onSave={() => saveEmail()}
            isSaveDisabled={!changedSinceSaved}
            onBack={backToManagePosts}
            onToggleSettingsPanel={onToggleDrawer}
            isToggleSettingsPanelDisabled={!settingsDrawerOpen}
            openGuidelines={() => setOpenContentGuidelines(true)}
        />, 
        [settingsDrawerOpen, changedSinceSaved, isEmailValid, openContentGuidelines, saveEmail, onClickSend]
    );

    const contentGuidelinePreview = React.useMemo(() => {
        return <ContentGuidelinePreview
            showPreview={openContentGuidelines}
            onClose={() => setOpenContentGuidelines(false)}
            isAuthoring={true}
            hintType={HintType.Post}
        />;
    }, [openContentGuidelines]);

    useEffect(() => {
        if (email.id !== "" && !isEmailIdPresent)
            redirectTo(`/${match.params.tenant}/admin/emails/edit/${email.id}`);
    }, [email.id, isEmailIdPresent]);

    const onChangeTranslatedContent = (newTranslations: Record<string, IEmailTranslatableContent>) => {
        const newSubject = Object.keys(newTranslations).map(lcid => ({ lcid, text: newTranslations[lcid].subject }));
        const newBody = Object.keys(newTranslations).map(lcid => ({ lcid, text: newTranslations[lcid].body }));

        updateEmail({ subject: newSubject, body: newBody });
    }

    const onTranslate = async (inputLanguage: string, outputLcid?: string) => {
        const languageToTranslateTo = outputLcid ?? activeLcid;
        const outputLanguage = availableLanguages?.find((language: ILanguage) => language.lcid === languageToTranslateTo);

        if (outputLanguage) 
        {
            try {
                await performTranslation(inputLanguage, outputLanguage);
            }
            catch {
                setErrorMessage("Something went wrong, please try again.");
            }
        }
    }

    const performTranslation = async (inputLanguage: string, outputLanguage: ILanguage) => {
        try
        {
            setIsTranslating(true);

            const translateReqBase = {
                fromLcid: inputLanguage,
                toLcid: outputLanguage.translateAs,
                contentType: TranslationContentType.Email
            }

            const subjectTranslateReq = {
                ...translateReqBase,
                text: memoizedEmailTranslations[inputLanguage].subject,
                textFormat: TranslationTextFormat.PlainText
            }

            const bodyTranslateReq = {
                ...translateReqBase,
                text: memoizedEmailTranslations[inputLanguage].body,
                textFormat: TranslationTextFormat.HTML
            };

            const subjectTranslation = await cognitiveServicesApi.translateText(subjectTranslateReq);
            const bodyTranslation = await cognitiveServicesApi.translateText(bodyTranslateReq);

            let newTranslations = { ...memoizedEmailTranslations };
            newTranslations[outputLanguage.lcid] = {
                subject: subjectTranslation.data.Translations[0].text,
                body: `<p><em>[${lcidMappings[outputLanguage.lcid].autoTranslateText}]</em></p>${bodyTranslation.data.Translations[0].text}`,
            };

            onChangeTranslatedContent(newTranslations);
        }
        catch (err) {
            throw err;
        }
        finally {
            setIsTranslating(false);
        }
    }

    const onTranslateAll = async (outputLanguages: ILcidTranslateDetails[]) => {
        const updatedTranslations = await translateAll(outputLanguages);

        if (updatedTranslations) {
            let existingTranslations = {...memoizedEmailTranslations};
            let languagesToUpdate = Object.keys(updatedTranslations);

            languagesToUpdate.forEach(lang => {
                const translationForLcid = updatedTranslations[lang];
                if (translationForLcid) {
                    existingTranslations[lang] = {
                        subject: translationForLcid.Subject,
                        body: `<p><em>[${lcidMappings[lang].autoTranslateText}]</em></p>${translationForLcid.Body}`,
                    };
                }
            })

            onChangeTranslatedContent(existingTranslations);
        }
    }

    const requiredTranslationFieldsHasContent = (lcid: string): boolean => 
        memoizedEmailTranslations[lcid] &&
        !!memoizedEmailTranslations[lcid].subject && 
        !!memoizedEmailTranslations[lcid].body

    const translationHasContent = (lcid: string): boolean => memoizedEmailTranslations[lcid] &&
        (memoizedEmailTranslations[lcid].subject.length > 0 || memoizedEmailTranslations[lcid].body.length > 0);

    return isLoading 
        ? <Loading />
        : <BasePage fullWidth>
            <AuthoringAppBar
                backAction={
                    isSmallAndSmaller
                        ? <></>
                        : <BackItem 
                            textStyle={{ fontWeight: 500 }} 
                            title="Back to Manage Emails"
                            onClick={backToManagePosts}
                        />
                }
                actions={getActions}
            />
            <div id="email-editor-content" className="authoring-page">
                <LoadingOverlay show={isSaving || isTranslating || isFetchingTranslation} styles={{ zIndex: 1201 }} />
                <TranslateAllIssueDialog
                    show={errorModalStatus.showModal}
                    onClose={() => errorModalStatus.updateModalOpenStatus(false)}
                    totalLanguages={errorModalStatus.expectedLanguages}
                    returnedTranslations={errorModalStatus.actualLanguages}
                />
                <EditorWrapperLayout>
                    <MainEditorLayout
                        paperProps={{
                            className: `main-editor-paper email-editor-paper main-editor-paper-${settingsDrawerOpen ? "open" : "closed"}`,
                            elevation: 0,
                        }}
                    >
                        <LanguageBarWithTranslateDialog
                            translations={memoizedEmailTranslations}
                            defaultContent={DEFAULT_EMAIL_TRANSLATABLE_CONTENT}
                            onChangeTranslatedContent={onChangeTranslatedContent}
                            onTranslate={onTranslate}
                            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                            transformOrigin={{ vertical: "top", horizontal: "left"}}
                            endIcon={<KeyboardArrowDown />}
                            calloutContentClassName=".email-editor-callout-content"
                            calloutClassName="email-editor-callout"
                            useLanguagesData={useLanguagesData}
                            requiredTranslationFieldsHasContent={requiredTranslationFieldsHasContent}
                            translationHasContent={translationHasContent}
                            contentType={TranslationContentType.Email}
                            showTranslateAllOption
                            translateAll={onTranslateAll}
                            translateAllDisabled={!translationHasContent(defaultLcid) || isTranslating || isFetchingTranslation}
                            disableLanguageButtons={isTranslating || isFetchingTranslation}
                        />
                        <EmailEditor 
                            updateEmail={updateEmail} 
                            activeLcid={activeLcid} 
                            onChangeFullscreen={isFullscreen => {
                                if (isFullscreen)
                                    setSettingsDrawerOpen(false);
                            }}
                        />
                    </MainEditorLayout>
                </EditorWrapperLayout>
                <PickerLocalization>
                    <EmailSettingsPanel
                        open={settingsDrawerOpen}
                        toggleOpen={onToggleDrawer}
                        fromEmailAddresses={fromEmailAddresses}
                        updateEmail={updateEmail}
                        onSuccessMessage={setSuccessMessage}
                        onErrorMessage={setErrorMessage}
                        onSaveEmail={saveEmail}
                        defaultAudienceCount={audienceCount}
                        isFetchingDefaultAudienceCount={isFetchingAudienceCount}
                    />
                </PickerLocalization>
            </div>
            <EmailPreview
                onClose={() => setShowPreview(false)}
                show={showPreview}
                email={email}
                activeLcid={activeLcid}
                tenantName={tenantName}
            />
            <ConfirmDialog
                title="Back to Manage Emails"
                confirmLabel="KEEP EDITING"
                denyLabel="EXIT"
                open={showUnsavedWarning}
                onClose={() => setShowUnsavedWarning(false)}
                onConfirm={() => setShowUnsavedWarning(false)}
                onDeny={redirectToManageEmails}
            >
                <div style={{ minWidth: 400 }}>
                    <div>
                        You have unsaved changes to your email. Changes will not be saved if you exit.</div>
                    <br />
                    <div>Do you want to continue?</div>
                </div>
            </ConfirmDialog>
            <ConfirmDialog
                title="Discard Changes"
                confirmLabel="SAVE CHANGES"
                denyLabel="DISCARD"
                open={showDiscardDialog}
                onClose={() => setShowDiscardDialog(false)}
                onConfirm={async () => {
                    setShowDiscardDialog(false);
                    saveEmail(manageEmailsUrl);
                }}
                onDeny={redirectToManageEmails}
            >
                <div style={{ minWidth: 400 }}>
                    You have unsaved changes to your draft. Would you like to save these changes before exiting?
                </div>
            </ConfirmDialog>
            {contentGuidelinePreview}
            <EmailOneLastCheck
                show={showOneLastCheck}
                onClose={() => setShowOneLastCheck(false)}
                sending={isSendingEmail}
                sendEmail={onSendEmail}
                onSaveEmail={saveEmail}
                onSuccessMessage={setSuccessMessage}
                onErrorMessage={setErrorMessage}
                activeLcid={activeLcid}
                defaultAudienceCount={audienceCount}
                isFetchingDefaultAudienceCount={isFetchingAudienceCount}
                tenantName={tenantName}
            />
        </BasePage>;
};

interface RouteParams {
    tenant: string;
    emailId: string;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: IEmailEditorPageProps & RouteComponentProps<RouteParams>) => ({
        ...ownProps,
        changedSinceSaved: state.emails.editor.changedSinceLastSaved,
        email: state.emails.editor.email,
        isFetching: state.emails.editor.isFetching,
        isSaving: state.emails.editor.isSaving,
        changedSinceLastSaved: state.emails.editor.changedSinceLastSaved,
        shouldOpenOneLastCheck: state.emails.editor.shouldOpenOneLastCheck,
        tenantName: state.settings.tenantSettings.displayName
    }),
    {
        redirectTo: push,
        getEmailSenderConfig: NewsletterActions.getConfig,
        getAudiences: AudienceActions.getAudiences,
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default withRouter(connector(EmailEditorPage));
